import { Outlet, useLocation } from 'react-router-dom';
import { AppSidebar } from '../../components/blocks/layout/app-sidebar';
// import { UProfileMenu } from '../../components/blocks/u-profile-menu';
import { SidebarProvider, useSidebar } from 'components/ui/sidebar';
// import { DynamicHeader } from './header/components/DynamicHeader';
// import { ModelSelector } from '../../components/blocks/model-selector';
import { AppFooter } from 'components/blocks/layout/app-footer';
import { SubscriptionDetails } from 'state/store/subscription/subscriptionStore';
import { useSubscriptionStore } from 'state/store/subscription';
import { PLANS } from 'constant/plan';

import GroupChatHeader from '../../features/group-chat/components/GroupChatHeader';
import { VoiceCallOverlay } from 'features/chat/components/VoiceCallOverlay';
import { /* BrandAmbientGlow, */ DarkModeGridOverlay } from '../../components/blocks/layout/brand-ambient-glow';
import { useVisualViewportBox } from 'hooks/use-mobile';
import { cn } from 'lib/utils';
import { useAssistantFileViewerStore } from 'state/store/chat/use-assistant-file-viewer-store';

export enum SidebarHeaderVariant {
  Logo = 'logo',
  Icons = 'icons',
}

export interface SidebarHeaderConfig {
  type: SidebarHeaderVariant;
  logo?: string;
  smallLogo?: string;
  open?: boolean;
  isMobile?: boolean;
  setOpenMobile?: (open: boolean) => void;
  strokeWidth?: number;
  toggleSidebar?: () => void;
  onNewChatClick?: () => void;
  onIncognitoClick?: () => void;
  currentPlan?: SubscriptionDetails | null;
  disableNewChat?: boolean;
}

export function getDefaultSidebarOpen() {
  if (typeof globalThis.window === 'undefined') {
    return true;
  }

  const width = globalThis.window.innerWidth;
  if (width < 768) {
    return true;
  }

  if (width < 1024) {
    const path = globalThis.window.location.pathname;
    return (
      path === '/chat' ||
      path.startsWith('/chat/') ||
      path === '/agentChat' ||
      path.startsWith('/agentChat/')
    );
  }

  // Desktop starts as an icon rail. Agents, Projects, and Recent Chats live in the panel beside it.
  return false;
}

export function isViewportLockedPath(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  return (
    firstSegment === 'chat' ||
    firstSegment === 'agentChat' ||
    firstSegment === 'media-library' ||
    firstSegment === 'dashboard' ||
    firstSegment === 'document-manager' ||
    firstSegment === 'project' ||
    (firstSegment === 'agent-details' &&
      (segments[2] === 'edit' ||
        segments[2] === 'configuration' ||
        segments[2] === 'integrations' ||
        segments[2] === 'conversations'))
  );
}

export default function MainLayout() {
  return (
    <SidebarProvider defaultOpen={getDefaultSidebarOpen()}>
      <MainLayoutContent />
    </SidebarProvider>
  );
}

function MainLayoutContent() {
  const { open, openMobile, setOpenMobile, configureOpen, chatsPanelOpen, isSidebarOverlay } = useSidebar();
  const currentPlan = useSubscriptionStore((state) => state.currentPlan);
  const showSplitPanel = !isSidebarOverlay && (configureOpen || chatsPanelOpen);
  const planName = currentPlan?.planCode ?? PLANS.UNLIMITED;
  const showCollapsedSidebarToggle =
    !isSidebarOverlay && !configureOpen && !chatsPanelOpen && planName !== PLANS.NOPLAN;

  const { pathname } = useLocation();
  const segments = pathname?.split('/').filter(Boolean);
  const firstSegment = segments?.[0] ?? undefined;
  const isEmailRoute = firstSegment === 'mail';
  const isChatRoute = firstSegment === 'chat' || firstSegment === 'agentChat';
  const isConfigurationRoute =
    firstSegment === 'agents' ||
    firstSegment === 'projects' ||
    firstSegment === 'media-library' ||
    firstSegment === 'dashboard' ||
    firstSegment === 'skills' ||
    firstSegment === 'tools' ||
    firstSegment === 'prompts' ||
    firstSegment === 'knowledge-base' ||
    firstSegment === 'integrations' ||
    firstSegment === 'usage' ||
    firstSegment === 'account' ||
    firstSegment === 'settings' ||
    firstSegment === 'identity-management';
  const isFileManagerRoute =
    firstSegment === 'media-library' || firstSegment === 'document-manager';
  const isProjectRoute = firstSegment === 'project';
  const isViewportLockedRoute = isViewportLockedPath(pathname);
  const pinToVisualViewport = isViewportLockedRoute && isSidebarOverlay;
  const visualViewport = useVisualViewportBox(pinToVisualViewport);
  const isDashboardConversation = useAssistantFileViewerStore(
    (state) => state.isDashboardConversation
  );
  const isDashboardCanvasDismissed = useAssistantFileViewerStore(
    (state) => state.isDashboardCanvasDismissed
  );
  const showDashboardCanvas = isDashboardConversation && !isDashboardCanvasDismissed;

  return (
    <div
      data-testid="app-shell"
      className={cn(
        'flex w-full max-w-[100vw] overflow-x-hidden',
        pinToVisualViewport
          ? 'fixed z-20 overflow-hidden'
          : 'relative min-h-svh'
      )}
      style={
        pinToVisualViewport
          ? {
              top: visualViewport.top,
              left: visualViewport.left,
              width: visualViewport.width,
              height: visualViewport.height,
              maxHeight: visualViewport.height,
            }
          : undefined
      }
    >
      {/* Mobile backdrop */}
      {openMobile && isSidebarOverlay && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity duration-300"
          onClick={() => setOpenMobile(false)}
        />
      )}

      <div className={cn(isSidebarOverlay ? '' : 'lg:absolute lg:left-0 lg:top-0 lg:z-40 lg:h-full')}>
        <AppSidebar
          type={SidebarHeaderVariant.Icons}
          strokeWidth={2}
        />
      </div>

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-background',
          showDashboardCanvas && 'relative',
          pinToVisualViewport ? 'min-h-0 h-full max-h-full' : isViewportLockedRoute ? 'h-svh max-h-svh' : 'min-h-svh',
          isSidebarOverlay
            ? 'w-full'
            : showCollapsedSidebarToggle
              ? 'lg:ml-0 lg:w-full'
              : showSplitPanel
                ? 'lg:ml-[var(--sidebar-total-width)] lg:w-[calc(100vw-var(--sidebar-total-width))]'
                : open
                  ? 'lg:ml-[var(--sidebar-width)] lg:w-[calc(100vw-var(--sidebar-width))]'
                  : 'lg:ml-[var(--sidebar-width-icon)] lg:w-[calc(100vw-var(--sidebar-width-icon))]',
          'transition-[margin-left,width] duration-300 ease-in-out'
        )}
      >
        <header
          data-testid="app-header"
          className={cn(
            'sticky top-0 z-20 shrink-0 bg-white dark:bg-background/95 dark:backdrop-blur-sm dark:supports-[backdrop-filter]:bg-background/60',
            showDashboardCanvas && 'md:w-1/3'
          )}
        >
          <div
            className={cn(
              'w-full min-w-0 px-2 py-3 sm:px-6 sm:py-4 md:px-8',
              showCollapsedSidebarToggle && 'lg:pl-14'
            )}
          >
            <GroupChatHeader />
          </div>
        </header>
        <div></div>
        <div
          data-testid="app-main-panel"
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col bg-white dark:bg-background w-full max-md:w-full',
            !showDashboardCanvas && 'relative',
            !showSplitPanel && open && !isSidebarOverlay && 'md:w-[calc(100vw-var(--sidebar-width))]',
            !isEmailRoute && !isChatRoute && !isConfigurationRoute && !isProjectRoute && 'px-4 sm:px-6 md:px-8 pb-0'
          )}
        >
          <DarkModeGridOverlay />
          {/* <BrandAmbientGlow /> */}
          <div
            data-testid="app-main-scroll"
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col',
              showDashboardCanvas
                ? 'overflow-visible'
                : 'relative z-10 overflow-y-auto overflow-x-hidden [scrollbar-width:thin] [&::-webkit-scrollbar]:w-0.5'
            )}
          >
            <Outlet />
          </div>
        </div>
        {/* Lives here, not in ChatPage: a fresh voice call navigates /chat → /chat/:id, which
            remounts the page; the call (and its overlay) must survive that. */}
        <VoiceCallOverlay />
        {!isFileManagerRoute && !isProjectRoute && firstSegment !== 'dashboard' && !showDashboardCanvas && (
          <AppFooter />
        )}
      </div>
    </div>
  );
}
